<template>
  <div class="subscription-fee-container">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>إدارة بنود الرسوم الدراسية</h2>
          <el-button type="primary" @click="handleCreate">
            <el-icon><Plus /></el-icon> إضافة بند رسوم جديد
          </el-button>
        </div>
      </template>

      <!-- --- الشرح الجديد هنا --- -->
      <el-alert
        title="ما هي هذه الواجهة؟"
        type="info"
        description="هذه الواجهة تسمح لك بتعريف جميع البنود المالية التي يمكن تحصيلها من الطلاب (مثل: رسوم التسجيل، رسوم الكتب، رسوم المواصلات، إلخ). كل بند تقوم بإنشائه هنا سيظهر لاحقًا في قائمة الرسوم عند إصدار فاتورة مالية للطالب."
        :closable="false"
        show-icon
        style="margin-bottom: 20px;"
      />

      <el-table :data="subscriptionFees" v-loading="loading" border style="width: 100%">
        <el-table-column prop="category" label="الفئة الرئيسية" />
        <el-table-column prop="sub_category" label="الفئة الفرعية" />
        <el-table-column prop="amount" label="المبلغ">
            <template #default="{ row }">
                {{ row.amount }} $
            </template>
        </el-table-column>
        <el-table-column label="الإجراءات" width="150" fixed="right">
          <template #default="{ row }">
            <el-button size="small" @click="handleEdit(row)">تعديل</el-button>
            <el-popconfirm
              title="هل أنت متأكد من حذف هذا البند؟"
              confirm-button-text="نعم"
              cancel-button-text="لا"
              @confirm="handleDelete(row.id)"
            >
              <template #reference>
                <el-button size="small" type="danger">حذف</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Dialog لإنشاء/تعديل الرسوم -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="40%" :close-on-click-modal="false">
      <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
        <el-form-item label="الفئة الرئيسية" prop="category">
          <el-input v-model="form.category" placeholder="مثال: رسوم دراسية، رسوم كتب" />
        </el-form-item>
        <el-form-item label="الفئة الفرعية (اختياري)" prop="sub_category">
          <el-input v-model="form.sub_category" placeholder="مثال: الفصل الأول، المستوى الأول" />
        </el-form-item>
        <el-form-item label="المبلغ ($)" prop="amount">
          <el-input-number v-model="form.amount" :min="0" :precision="2" :step="10" style="width: 100%;" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">إلغاء</el-button>
          <el-button type="primary" @click="submitForm" :loading="formLoading">
            {{ form.id ? 'تحديث' : 'إنشاء' }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage, ElPopconfirm } from 'element-plus';
import { Plus } from '@element-plus/icons-vue';

export default {
  name: 'SubscriptionFeeManagement',
  components: { Plus, ElPopconfirm },
  data() {
    return {
      api: null,
      loading: true,
      subscriptionFees: [],
      dialogVisible: false,
      formLoading: false,
      form: this.getInitialForm(),
      rules: {
        category: [{ required: true, message: 'الفئة الرئيسية مطلوبة', trigger: 'blur' }],
        amount: [{ required: true, message: 'المبلغ مطلوب', trigger: 'blur' }],
      },
    };
  },
  computed: {
    dialogTitle() {
      return this.form.id ? 'تعديل بند الرسوم' : 'إنشاء بند رسوم جديد';
    },
  },
  created() {
    this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    });
    this.fetchSubscriptionFees();
  },
  methods: {
    getInitialForm() {
      return { id: null, category: '', sub_category: '', amount: 0 };
    },
    async fetchSubscriptionFees() {
      this.loading = true;
      try {
        const response = await this.api.get('/subscription-fees');
        this.subscriptionFees = response.data;
      } catch (error) {
        ElMessage.error('فشل في جلب بنود الرسوم.');
      } finally {
        this.loading = false;
      }
    },
    handleCreate() {
      this.form = this.getInitialForm();
      this.dialogVisible = true;
    },
    handleEdit(fee) {
      this.form = { ...fee };
      this.dialogVisible = true;
    },
    async submitForm() {
      if (!this.$refs.formRef) return;
      await this.$refs.formRef.validate(async (valid) => {
        if (!valid) return;
        this.formLoading = true;
        try {
          if (this.form.id) {
            await this.api.put(`/subscription-fees/${this.form.id}`, this.form);
            ElMessage.success('تم تحديث البند بنجاح!');
          } else {
            await this.api.post('/subscription-fees', this.form);
            ElMessage.success('تم إنشاء البند بنجاح!');
          }
          this.dialogVisible = false;
          this.fetchSubscriptionFees();
        } catch (error) {
          const errorMsg = error.response?.data?.errors ? Object.values(error.response.data.errors)[0][0] : (error.response?.data?.message || 'فشلت العملية.');
          ElMessage.error(errorMsg);
        } finally {
          this.formLoading = false;
        }
      });
    },
    async handleDelete(id) {
      try {
        await this.api.delete(`/subscription-fees/${id}`);
        ElMessage.success('تم حذف البند بنجاح!');
        this.fetchSubscriptionFees();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || 'فشل في حذف البند.');
      }
    },
  },
};
</script>

<style scoped>
.subscription-fee-container { padding: 20px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
</style>
